export const Util = {
    /**
     * Funkcja sprawdzająca, czy przeglądarka wspiera podstawowe elementary, z których korzysta Konva.js.
     * @returns {boolean}
     */
    supportsCanvasContext2d() {
        return typeof HTMLCanvasElement !== "undefined" &&
            typeof CanvasRenderingContext2D !== "undefined";
    },

    /**
     * Funkcja sprawdzająca, czy przeglądarka wspiera natywne filtry Context2D.
     * @returns {boolean}
     */
    supportsContextFilters() {
        return "filter" in CanvasRenderingContext2D.prototype;
    },

    /**
     * Funkcja przekształca wartość procentową zapisaną w postaci "50%" odpowiadającą liczbę zmiennoprzecinkową.
     * @param {string} percentage
     * @returns {number}
     */
    normalizeNumberPercentage(percentage) {
        let normalized = parseFloat(percentage);

        if (/%\s*?$/i.test(percentage)) {
            normalized /= 100;
        }

        return normalized;
    },

    /**
    * Funkcja zwraca wartość będącą zadaną ilością jednostek standardowych dla danych width/height strony.
    * @param {number} coutOfUnits
    * @returns {number}
    */
    universalStandardUnits(coutOfUnits, pageWidth, pageHeight) {
        return (coutOfUnits * (((pageHeight + pageWidth) / 2) / 100));
    },

    /**
     * Funkcja przeskalowuje d-path SVG, mnożąc wartości liczbowe przez zadaną skalę.
     * Funkcja nie modyfikuje referencji podanego path, ale zwraca nowy przeskalowany path.
     * @param {string} path
     * @param {number} scale
     * @returns {string}
     */
    scaleSvgPath(path, scale) {
        // Regular expression to match numbers (supports integers, decimals, and negative numbers)
        const regex = /-?(?:\d*\.\d+|\d+)/g;

        // Replace each numeric match with its scaled value
        const scaledPath = path.replace(regex, (match) => {
            const num = parseFloat(match);
            if (!isNaN(num)) {
                return (Math.round((num * scale*10000))/10000).toString();
            }
            return match;
        });

        return scaledPath;
    },

    /**
     * Funkcja zwraca klon obiektu Konva, ale pozbawionego kluczowych atrybutów np.id, name, fillPatternImage etc. - pozostaje tylko kształt, wielkość i pozycja.
     * @param {Konva.Shape} item
     * @returns {Konva.Shape}
     */
    getDummyClone(item) {
        const strokeOffset = Util.getStrokeOffset(item);
        return item.clone({
            id: null,
            name: "dummyClone",
            strokeEnabled: false,
            listening: false,
            fillPatternImage: null,
            photoId: null,
            crop: null,
            shadowEnabled:false,
            fillPriority: "color",
            scaleX: (item.width() - strokeOffset) * item.scaleX() / item.width(),
            scaleY: (item.height() - strokeOffset) * item.scaleY() / item.height(),   
            x: item.x() + (strokeOffset / 2) * item.scaleX(),  
            y: item.y() + (strokeOffset / 2) * item.scaleY()
        });
    },

    /**
     * Metoda nadaje obiektowi wypełnienie gradientem dwu stopniowym.
     * Punkty zaczepienia zgodnie z rysunkiem -> https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/createLinearGradient
     * @param {Konva.Shape} item
     * @param {number} startPositionAnchor - y0 (patrz rysunek), podawane w układzie współrzędnych layoutu (dla placeholderów)  
     * @param {number} endPositionAnchor - y1 (patrz rysunek), podawane w układzie współrzędnych layoutu (dla placeholderów)
     * @param {string} color1 - u góry
     * @param {string} color2 - u dołu
     */
    apply2StepLinearGradient(item, startPositionAnchor, endPositionAnchor, color1, color2) {
        //#region Info
        /**
         * W skrócie - działa tak samo jak fill pattern image --> w układzie współżędnych obiektu
         * (zeskalowane zgodnie z jego skalą), czyli punkt start/koniec trzeba przenieść do tego układu współżędnych.
         * W przypadku nie prostokątów należy pamiętać o konieczności naprawienia początku układu współżędnych tego obiektu
         * (nie offset nie załatwia sprawy - analogia do fillPatternX/Y tylko tu trzeba ręcznie doliczyć do pozycji punktów)
         *
         * PS. offset jest miarą wewnętrzną czyli podlega skalowaniu tak jak width i height
         */
        //#endregion
        const w = item.width(); // zmienna pomocnicza
        // #region ustalenie bazowych punktów gradientu
        const startPointY = startPositionAnchor - (item.getParent().y() / item.scaleY()); // w układzie współżędnych w skali obiektu
        const endPointY = (endPositionAnchor / item.scaleY()) + startPointY; // koniec gradientu w układzie współrzędnych w skali obiektu
        // #endregion

        // #region Naprawa wewnętrznego układu układu współrzędnych obiektu - odpowiednik fillPatternX/Y
        let gradientOffset = 0;
        if (item.getClassName() === "Ellipse") {
            gradientOffset = item.radiusY();
        }
        else if (item.getClassName() === "Path" || item.getClassName() === "Line") {
            const clientRect = item.getClientRect({ skipTransform: true, skipStroke: true });
            gradientOffset = -clientRect.y;
        }
        // #endregion

        // ustawienie gradientu z poprawkami dla punktów
        item.setAttrs({
            fillPriority: "linear-gradient",
            fillLinearGradientStartPoint: { x: (w / 2), y: startPointY - gradientOffset },
            fillLinearGradientEndPoint: { x: (w / 2), y: endPointY - gradientOffset },
            fillLinearGradientColorStops: [0, color1, 1, color2]
        });
    },

    /**
     * Funkcja zwraca skalę i pozycję obiektu, po których zastosowaniu, zmieści się zadanych rozmiarach i będzie względem nich wyśrodkowany.
     * Obiekt jest centrowany. Skala nakładana proporcjonalnie. Zwraca skalę i pozycję.
     * @param {number} itemWidth - szerokość obiektu, który ma być skalowany
     * @param {number} itemHeight - wysokość obiektu, który ma być skalowany
     * @param {number} boxWidth - szerokość obszaru, w którym ma się zmieścić obiekt
     * @param {number} boxHeight - wysokość obszaru, w którym ma się zmieścić obiekt
     * @return {{scale:number, x:number, y:number}}
     */
    getCentralFitInParams(itemWidth, itemHeight, boxWidth, boxHeight) {
        const scale = Math.min(boxWidth / itemWidth, boxHeight / itemHeight);
        return {
            scale: scale,
            x: (boxWidth - itemWidth * scale) / 2,
            y: (boxHeight - itemHeight * scale) / 2
        }
    },

    /**
     * Funkcja zwraca skalę i pozycję obiektu, po których zastosowaniu, będzie efekt fit-out z wyśrodkowaniem.
     * Obiekt jest centrowany. Skala nakładana proporcjonalnie. Zwraca skalę i pozycję.
     * @param {number} itemWidth - szerokość obiektu, który ma być skalowany
     * @param {number} itemHeight - wysokość obiektu, który ma być skalowany
     * @param {number} boxWidth - szerokość obszaru, w którym ma się zmieścić obiekt
     * @param {number} boxHeight - wysokość obszaru, w którym ma się zmieścić obiekt
     * @return {{scale:number, x:number, y:number}}
     */
    getCentralFitOutParams(itemWidth, itemHeight, boxWidth, boxHeight) {
        const scale = Math.max(boxWidth / itemWidth, boxHeight / itemHeight);
        return {
            scale: scale,
            x: (boxWidth - itemWidth * scale) / 2,
            y: (boxHeight - itemHeight * scale) / 2
        }
    },

    /**
     * Metoda ustawia image na środku obiektu, skalując go do maksymalnego rozmiaru.
     * Jeśli image w maksymalnym rozmiarze jest większy niż obiekt, jest on pomniejszany.
     * Metoda ustawia obliczoną pozycję i skalę image.
     * @param {Konva.Shape} item
     * @param {Konva.Image} image
     * @param {number} imageMaxSize
     */
    centerImageOnItem(item, image, imageMaxSize) {
        let scale = 1;
        if (imageMaxSize > Math.min(item.width() * item.scaleX(), item.height() * item.scaleY())) { 
            const scaleX = (item.width() * item.scaleX()) / image.width();
            const scaleY = (item.height() * item.scaleY()) / image.height();
            scale = Math.min(scaleX, scaleY);
        } else {
            scale = imageMaxSize / Math.max(image.width(), image.height());
        }
        image.size({
            width: image.width() * scale,
            height: image.height() * scale
        });
        image.position({
            x: (item.width() * item.scaleX() - image.width()) / 2,
            y: (item.height() * item.scaleY() - image.height()) / 2
        });
    },

    formatAsKonvaName: function (tag) {
        return `.${tag}`;
    },

    formatAsKonvaId: function (tag) {
        return `#${tag}`;
    },

    /**
     * Funkcja wyszukuje (i zwraca) najbardziej dopasowany rozmiar czcionki do zadanej wysokości textboxa.
     * @param {Konva.Text} item
     * @param {number} destHeight - docelowa wysokość textboxa, do której ma być dopasowana czcionka
     * @param {number} minFontSize - minimalny rozmiar czcionki, poniżej którego nie będzie szukać
     * @param {number} maxFontSize - maksymalny rozmiar czcionki, powyżej którego nie będzie szukać
     * @returns {number}
     */
    binarySearchFittingFontSize(item, destHeight, minFontSize, maxFontSize) {
        let bestFitFontSize = minFontSize;
        while (minFontSize < maxFontSize) {
            const p = Math.floor((minFontSize + maxFontSize) / 2);
            item.fontSize(p);
            //textItem.text(text); działa bez tego
            const currentHeight = item.height();
            if (currentHeight <= destHeight) {
                bestFitFontSize = p; // found a better fit
                minFontSize = p + 1; // try a larger font size
            } else {
                maxFontSize = p; // try a smaller font size
            }
        }
        return bestFitFontSize; // return the best fit font size found
    },

    /**
     * Funkcja ustawia tekst w textboxie, i dopasowuje rozmiar czcionki tak, aby cały tekst był widoczny.
     * Wyjątek stanowi sytuacja kiedy text jest tak obszerny że musiałaby by zostać zastosowana czcionka mniejsza niż absoluteMinFontSize.
     * Wtedy tekst jest wyświetlany w wielkości absoluteMinFontSize i stosowana a jest na końcu eliclipse ("...").
     * @param {any} item
     * @param {any} text
     * @param {any} preferredFontSize - preferowana wielkość czcionki, jeśli nie jest podana, to zostanie użyta aktualnie ustawiona wielkość czcionki na obiekcie.
     * @param {any} absoluteMinFontSize 
     */
    fitText(item, text, preferredFontSize, absoluteMinFontSize) {
        // 1 - save the current text parameters
        const destHeight = item.height(); //razem z paddingiem
        const lastFontSize = item.fontSize(); // save the last font size
        const destFontSize = preferredFontSize || lastFontSize; // get the defined font size or the last font size

        // 2 - make a test - is new text fits the textbox in the last font size
        item.height('auto'); // reset height to auto to measure the text height correctly
        item.text(text); // set the text to measure
        const currentHeight = item.height(); // get the current height determinated by fonsize and text content

        // 3.1 - if it fits in the last font size 
        if (currentHeight <= destHeight) {
            if (lastFontSize !== destFontSize) { // if the last font size isn't than defined font size

                // 4 - check if text will fit in the defined font size
                item.fontSize(destFontSize);
                const heightInDestFontSize = item.height(); // get the height in the defined font size

                // 5.1 - if it doesn't find font size in <lastFontSize; definedFontSize> range that fits
                if (heightInDestFontSize > destHeight) {
                    item.fontSize(Util.binarySearchFittingFontSize(item, destHeight, lastFontSize, destFontSize)); // find the best fit font size in the range of <lastFontSize; definedFontSize>)
                }
            }
        }// 3.3 - else if it doesn't fit in the last font size and the last font size is greater than the absolute minimum font size - we need to decrease the font size until the text fits
        else if (lastFontSize > absoluteMinFontSize) {
            const minScale = (destHeight - 2 * item.padding()) / (currentHeight - 2 * item.padding());
            const minFontSize = Math.max(lastFontSize * minScale, absoluteMinFontSize);
            item.fontSize(Util.binarySearchFittingFontSize(item, destHeight, minFontSize, lastFontSize));
        }
        // restore the original textbox height and re-render the text
        item.height(destHeight);
        item.text(text);
    },

    /**
     * Funkcja zwracająca stroke offset w układzie współrzędnych obiektu. 
     * @param {Konva.Shape} item
     * @returns {number}
     */
    getStrokeOffset(item) {
        return item.hasStroke() ? item.strokeWidth() : 0;
    },

    /**
     * Funkcja zwraca informację, czy dwie liczby są sobie równe po zaokrągleniu do zadanej precyzji.
     * @param {number} a
     * @param {number} b
     * @param {number} decimalPrecision - ilość miejsc po przecinku, do których mają być porównywane liczby
     * @returns {boolean}
     */
    equalsInRound(a, b, decimalPrecision = 4) {
        const factor = Math.pow(10, decimalPrecision);
        return Math.round(a * factor) === Math.round(b * factor);
    }
}
